import {
  LINK_TYPE_OPTIONS,
  SECTOR_TEMPLATES,
  type DraftTargetLink,
  type SectorId,
} from '@myplayer/schema';
import { IconExternalLink, IconPlus } from '@myplayer/ui';
import { useState, type FormEvent, type ReactElement } from 'react';
import { useAuth } from './auth-context.js';
import {
  Button,
  Input,
  ModalDialog,
  SearchableMultiSelect,
  Select,
  Switch,
  Tag,
  ToastNotification,
} from './components/ui/primitives.js';
import { COUNTRY_REGION_OPTIONS, LANGUAGE_OPTIONS } from './locale-region-options.js';
import {
  ProductScheduleSection,
  type ProductScheduleSectionProps,
} from './product-schedule-section.js';
import { formatPlainLinkTypeLabel } from './products-helpers.js';
import { formatCapabilityRestrictionNotice } from './rbac-ui.js';
import { resolveExampleBrandHostname } from './use-workspace-identifiers.js';

export { ProductScheduleSection, type ProductScheduleSectionProps };

export interface ProductLinkEditorModalProps {
  readonly open: boolean;
  readonly editingIndex: number | null;
  readonly savingDraft: boolean;
  readonly linkType: string;
  readonly setLinkType: (val: string) => void;
  readonly linkTitle: string;
  readonly setLinkTitle: (val: string) => void;
  readonly linkUrl: string;
  readonly setLinkUrl: (val: string) => void;
  readonly linkLanguage: string;
  readonly setLinkLanguage: (val: string) => void;
  readonly linkRegion: string;
  readonly setLinkRegion: (val: string) => void;
  readonly linkIsDefault: boolean;
  readonly setLinkIsDefault: (val: boolean) => void;
  readonly linkFormError: string | null;
  readonly effectiveResolverHost?: string | undefined;
  readonly onSaveLinkForm: (e: FormEvent) => Promise<void>;
  readonly onCancelEdit: () => void;
}

export function ProductLinkEditorModal(p: ProductLinkEditorModalProps): ReactElement {
  const hostForPlaceholder = resolveExampleBrandHostname(p.effectiveResolverHost);
  return (
    <ModalDialog
      open={p.open}
      presentation="center"
      title={p.editingIndex !== null ? `Edit link #${String(p.editingIndex + 1)}` : 'Add link'}
      subtitle="Configure link type, languages, and target regions"
      icon={<IconExternalLink />}
      maxWidthClassName="max-w-2xl"
      submitting={p.savingDraft}
      onClose={p.onCancelEdit}
      data-testid="extra-link-modal"
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={p.savingDraft}
            onClick={p.onCancelEdit}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="product-link-editor-form"
            variant="primary"
            size="sm"
            data-testid="save-extra-link-button"
            disabled={p.savingDraft}
          >
            <IconPlus />
            <span>{p.editingIndex !== null ? 'Save link' : 'Add link'}</span>
          </Button>
        </>
      }
    >
      <form
        id="product-link-editor-form"
        onSubmit={(e) => void p.onSaveLinkForm(e)}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="link-type-select"
              className="mb-1.5 block text-[12px] font-medium text-fg-secondary"
            >
              Link type
            </label>
            <Select
              id="link-type-select"
              value={p.linkType}
              onChange={(e) => p.setLinkType(e.target.value)}
            >
              {LINK_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {formatPlainLinkTypeLabel(opt.value)}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label
              htmlFor="link-title-input"
              className="mb-1.5 block text-[12px] font-medium text-fg-secondary"
            >
              Link title
            </label>
            <Input
              id="link-title-input"
              value={p.linkTitle}
              onChange={(e) => p.setLinkTitle(e.target.value)}
              placeholder="Allergen & Nutritional Guide"
            />
          </div>
          <div className="sm:col-span-2">
            <label
              htmlFor="link-url-input"
              className="mb-1.5 block text-[12px] font-medium text-fg-secondary"
            >
              Web address (HTTPS)
            </label>
            <Input
              id="link-url-input"
              value={p.linkUrl}
              onChange={(e) => p.setLinkUrl(e.target.value)}
              placeholder={`https://${hostForPlaceholder}/allergens`}
            />
          </div>
          <div>
            <label
              htmlFor="link-language-input"
              className="mb-1.5 block text-[12px] font-medium text-fg-secondary"
            >
              Languages
            </label>
            <SearchableMultiSelect
              id="link-language-input"
              value={p.linkLanguage}
              onChange={(val) => p.setLinkLanguage(val)}
              options={LANGUAGE_OPTIONS}
              placeholder="Select languages..."
              searchPlaceholder="Search languages..."
            />
          </div>
          <div>
            <label
              htmlFor="link-region-input"
              className="mb-1.5 block text-[12px] font-medium text-fg-secondary"
            >
              Countries &amp; regions
            </label>
            <SearchableMultiSelect
              id="link-region-input"
              value={p.linkRegion}
              onChange={(val) => p.setLinkRegion(val)}
              options={COUNTRY_REGION_OPTIONS}
              placeholder="Select countries or regions..."
              searchPlaceholder="Search countries or regions..."
            />
          </div>
        </div>
        <div className="pt-1">
          <Switch
            id="link-is-default-toggle"
            checked={p.linkIsDefault}
            onChange={(e) => p.setLinkIsDefault(e.target.checked)}
            label="Use as main scanning link"
          />
        </div>
        {p.linkFormError !== null && (
          <p
            role="alert"
            data-testid="link-editor-error"
            className="rounded-control border border-status-error-border bg-status-error-bg p-2.5 text-[12px] font-medium text-status-error"
          >
            {p.linkFormError}
          </p>
        )}
      </form>
    </ModalDialog>
  );
}

export interface ProductLinksEditorProps {
  readonly productTitle?: string;
  readonly productGtin?: string;
  readonly workingLinks?: readonly DraftTargetLink[] | undefined;
  readonly editPrimaryUrl: string;
  readonly setEditPrimaryUrl: (val: string) => void;
  readonly editSector: SectorId;
  readonly setEditSector: (val: SectorId) => void;
  readonly canEdit: boolean;
  readonly savingDraft: boolean;
  readonly linkFormError: string | null;
  readonly linkFormSuccess: string | null;
  readonly effectiveResolverHost?: string;
  readonly onOpenAddLinkModal?: (() => void) | undefined;
  readonly onSaveProductEdit: (e: FormEvent) => Promise<unknown>;
  readonly onCancelProductEdit: () => void;
}

export function ProductLinksEditor(p: ProductLinksEditorProps): ReactElement {
  const { session } = useAuth();
  const hostForPlaceholder = resolveExampleBrandHostname(p.effectiveResolverHost);
  const sectorEntries = Object.values(SECTOR_TEMPLATES);
  const activeLinks = (p.workingLinks ?? []).filter((l) => l.removed !== true);
  const matchedLink = activeLinks.find((l) => l.href === p.editPrimaryUrl);
  const [forceCustomUrl, setForceCustomUrl] = useState(() => matchedLink === undefined);
  const showCustomUrlInput =
    activeLinks.length === 0 || forceCustomUrl || matchedLink === undefined;

  return (
    <ModalDialog
      open={true}
      presentation="center"
      title={p.productTitle ? `Edit ${p.productTitle}` : 'Edit product link'}
      {...(p.productGtin !== undefined ? { subtitle: `GTIN ${p.productGtin}` } : {})}
      icon={<IconExternalLink />}
      maxWidthClassName="max-w-2xl"
      submitting={p.savingDraft}
      onClose={p.onCancelProductEdit}
      data-testid="link-editor-card"
      footer={
        p.canEdit ? (
          <>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              data-testid="cancel-product-edit-button"
              onClick={p.onCancelProductEdit}
              disabled={p.savingDraft}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="product-primary-edit-form"
              variant="primary"
              size="sm"
              data-testid="save-product-edit-button"
              disabled={p.savingDraft}
            >
              <span>{p.savingDraft ? 'Saving...' : 'Save'}</span>
            </Button>
          </>
        ) : undefined
      }
    >
      <div className="space-y-4">
        {!p.canEdit && (
          <p
            role="status"
            data-testid="links-editor-readonly-notice"
            className="text-[12px] text-fg-secondary"
          >
            {formatCapabilityRestrictionNotice({
              capability: 'identifier:create_stage_update',
              actionSummary: 'edit product links',
              callerRole: session?.personaRole,
            })}
          </p>
        )}
        {p.canEdit && (
          <form
            id="product-primary-edit-form"
            onSubmit={(e) => void p.onSaveProductEdit(e)}
            className="space-y-4"
          >
            {activeLinks.length > 0 && (
              <div className="space-y-1.5">
                <label
                  htmlFor="main-link-select"
                  className="block text-[13px] font-semibold text-fg"
                >
                  Main scan destination link
                </label>
                <Select
                  id="main-link-select"
                  data-testid="main-link-select"
                  value={!showCustomUrlInput && matchedLink ? matchedLink.href : '__CUSTOM__'}
                  onChange={(e) => {
                    if (e.target.value === '__CUSTOM__') {
                      setForceCustomUrl(true);
                    } else if (e.target.value.length > 0) {
                      setForceCustomUrl(false);
                      p.setEditPrimaryUrl(e.target.value);
                    }
                  }}
                >
                  {activeLinks.map((l, idx) => (
                    <option key={`${l.href}-${String(idx)}`} value={l.href}>
                      {l.title} ({formatPlainLinkTypeLabel(l.linkType)})
                    </option>
                  ))}
                  <option value="__CUSTOM__">Enter a custom HTTPS web address…</option>
                </Select>
                {!showCustomUrlInput && matchedLink !== undefined && (
                  <div
                    data-testid="selected-main-link-preview"
                    className="flex min-w-0 items-center gap-2 rounded-control border border-subtle bg-surface-muted px-3 py-2 text-[12px]"
                  >
                    <Tag variant="outline">{formatPlainLinkTypeLabel(matchedLink.linkType)}</Tag>
                    <span className="truncate font-mono text-fg-secondary">{matchedLink.href}</span>
                  </div>
                )}
              </div>
            )}
            {showCustomUrlInput && (
              <div className="space-y-1.5">
                <label
                  htmlFor="primary-scan-url-input"
                  className="block text-[13px] font-semibold text-fg"
                >
                  Where should scanning go?
                </label>
                <Input
                  id="primary-scan-url-input"
                  data-testid="primary-scan-url-input"
                  value={p.editPrimaryUrl}
                  onChange={(e) => p.setEditPrimaryUrl(e.target.value)}
                  placeholder={`https://${hostForPlaceholder}/products/item`}
                />
                <p className="text-[12px] text-fg-secondary">
                  Customers who scan this product barcode go straight to this web address.
                </p>
              </div>
            )}
            <div className="space-y-1.5">
              <label
                htmlFor="product-sector-select"
                className="block text-[13px] font-semibold text-fg"
              >
                Sector
              </label>
              <Select
                id="product-sector-select"
                value={p.editSector}
                onChange={(e) => p.setEditSector(e.target.value as SectorId)}
              >
                {sectorEntries.map((sec) => (
                  <option key={sec.sectorId} value={sec.sectorId}>
                    {sec.name}
                  </option>
                ))}
              </Select>
            </div>
            {p.linkFormError !== null && (
              <p
                role="alert"
                data-testid="link-editor-error"
                className="rounded-control border border-status-error-border bg-status-error-bg p-2.5 text-[12px] font-medium text-status-error"
              >
                {p.linkFormError}
              </p>
            )}
            <ToastNotification data-testid="link-editor-success" message={p.linkFormSuccess} />
          </form>
        )}
      </div>
    </ModalDialog>
  );
}
